// src/components/KcodeTablesRow.tsx
// 疾患タブ最下部「点数表区分番号」 表 6/7/8 を 3 列横並びで表示する component。
//
// レイアウト: grid grid-cols-3 gap-2 (= フル HD で 3 表横並び、 狭画面 (lg 未満) は縦積み)
//
// 各表:
//   - 列: 点数表区分番号 (kcode) / 名称 / 実績件数 (n_cases)
//   - 行: 実績 > 0 のもののみ (= server SQL 側で HAVING n_cases > 0)
//   - ソート: n_cases 降順
//   - 最大行高: max-h-[360px] でスクロール (約 20 行表示)
//
// 共通 filter (期間 / dep / iryo / yotei / dpcfy / dpc6 / admdrcd) は他の表と同期。
import { useState } from "react";
import { useDiseaseRankingKcode } from "../lib/useDiseaseRankingKcode";
import { CsvButton } from "./CsvButton";
import { useFilters, dpcChainPatch } from "../store";
import { useDpcAgg } from "../lib/useDpcAgg";
import { useSort, SortTh } from "../lib/useSort";
import type { DiseaseRankingKcodeRow, KcodeType } from "@shared/api";

// 2026-05-28 ユーザ指示: 表 6/7/8 の行クリックで top_dpc を 上位階層 全部に
// 流し込んで drill-down + DpcTensuPanel を連動絞り込みする。
// store の setter は下位 reset する副作用があるため、 useFilters.setState で
// 一度に全 path を上書きする (= dpc6 / 10 / 11 / 12 / 14 を一括設定)。
function applyDpcFromTopDpc(topDpc: string | null) {
  if (!topDpc) return;
  const code = topDpc.trim();
  if (code.length < 6) return;
  useFilters.setState({
    dpc6:          code.length >= 6  ? code.slice(0, 6)  : null,
    ...dpcChainPatch(code, Math.min(code.length, 14)),
  });
}

// [2026-09-29 ユーザ指示] 退院数 = 1 退院を 1 つの K にだけ数える (合計が主表の退院数と一致する)。
const MAIN_NOTE: Record<KcodeType, string> = {
  ope:   "この手術 K を「その退院の手術」として数えた退院の数。1 退院は 1 行にだけ入る (同じ退院に複数あるときはコード順で先頭・元データに順番が無いため)。合計 + 下の「該当なし」= 主表の退院数",
  proc1: "この処置を「その退院の DPC 11 桁目 (手術・処置等1) を決めた処置」として数えた退院の数 (公式対応表で照合)。1 退院は 1 行にだけ入る (候補が複数あるときはコード順で先頭)。合計 + 下の「該当なし」= 主表の退院数",
  proc2: "この処置を「その退院の DPC 12 桁目 (手術・処置等2) を決めた処置」として数えた退院の数 (公式対応表で照合)。1 退院は 1 行にだけ入る (候補が複数あるときはコード順で先頭)。合計 + 下の「該当なし」= 主表の退院数",
};
const NONE_LABEL: Record<KcodeType, string> = {
  ope:   "（手術なし・DPC定義外の手術のみ）",
  proc1: "（11桁目を決めた処置なし）",
  proc2: "（12桁目を決めた処置なし）",
};

interface KcodeTableProps {
  type:  KcodeType;
  title: string;
  subtitle: string;
  branchOnly?: boolean;
}

function KcodeTable({ type, title, subtitle, branchOnly = false }: KcodeTableProps) {
  const { data, isLoading, error } = useDiseaseRankingKcode(type, true, branchOnly);
  const rows: DiseaseRankingKcodeRow[] = data?.rows ?? [];
  const s = useSort(rows);
  const f = useFilters();
  // 現在選択中の DPC14 と一致する行はハイライト (= どの行が絞り込み元か視認)
  const selectedDpc14 = f.selectedDpc14;
  const agg = useDpcAgg();   // [2026-09-28] 主表と同じ 期間累計/月平均

  return (
    <section className="card p-2 flex flex-col min-w-0">
      <div className="flex items-center gap-1 mb-1 flex-wrap">
        <h3 className="text-xs font-semibold">{title}</h3>
        <span className="text-[10px] text-slate-500">{subtitle}</span>
        <span className="text-[10px] text-emerald-700">件数={agg.label}</span>
        {data && (
          <span className="perf-pill text-[10px]">
            {rows.length} 件 / {data.sqlMs} ms
          </span>
        )}
        {isLoading && <span className="text-[10px] text-slate-400">loading…</span>}
        <CsvButton
          rows={rows.map((r) => ({
            Kコード: r.kcode,
            名称:           r.name,
            退院数:         r.n_main,
            件数:           r.n_cases,
          })).concat(data ? [{ Kコード: "", 名称: NONE_LABEL[type], 退院数: data.none_main, 件数: 0 }] : [])}
          filename={`kcode_${type}`}
          style={{ marginLeft: "auto" }}
        />
      </div>

      {error && (
        <div className="text-[11px] text-red-700 mb-1">
          取得エラー: {(error as Error).message}
        </div>
      )}

      <div className="overflow-auto max-h-[360px] border border-slate-200 rounded">
        <table className="text-[11px] border-collapse w-full">
          <thead className="sticky top-0 bg-slate-100 z-10">
            <tr>
              <SortTh s={s} sortKey="kcode" get={(r) => (r as DiseaseRankingKcodeRow).kcode} className="px-1.5 py-1 border-b border-slate-200 text-left whitespace-nowrap">Kコード</SortTh>
              <SortTh s={s} sortKey="name" get={(r) => (r as DiseaseRankingKcodeRow).name} className="px-1.5 py-1 border-b border-slate-200 text-left">名称</SortTh>
              {/* [2026-09-29 ユーザ指示] 最頻DPC 列は削除。退院数 (メインとして数えた退院・合計=主表の退院数) と 件数 (実施のあった退院・併施込み) を並べる */}
              <SortTh s={s} sortKey="n_main" get={(r) => (r as DiseaseRankingKcodeRow).n_main} className="px-1.5 py-1 border-b border-slate-200 text-right whitespace-nowrap"
                      title={MAIN_NOTE[type]}>退院数</SortTh>
              <SortTh s={s} sortKey="n_cases" get={(r) => (r as DiseaseRankingKcodeRow).n_cases} className="px-1.5 py-1 border-b border-slate-200 text-right whitespace-nowrap"
                      title="この K が実施された退院の数 (併施も含む。1 退院が複数の行に入るので合計は退院数と一致しない)">件数</SortTh>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !isLoading && (
              <tr>
                <td colSpan={4} className="text-center text-slate-400 py-3">
                  該当データなし
                </td>
              </tr>
            )}
            {s.rows.map((r) => {
              const isActive = !!r.top_dpc && selectedDpc14 != null && r.top_dpc === selectedDpc14;
              const clickable = !!r.top_dpc;
              return (
                <tr
                  key={r.kcode}
                  className={
                    "border-b border-slate-100 " +
                    (isActive
                      ? "bg-amber-100 hover:bg-amber-200 "
                      : clickable
                        ? "hover:bg-emerald-50 cursor-pointer "
                        : "hover:bg-slate-50 ")
                  }
                  onClick={clickable ? () => applyDpcFromTopDpc(r.top_dpc) : undefined}
                  title={clickable ? `クリックで この K を伴う退院で最も多い DPC ${r.top_dpc} に絞り込み` : "最頻 DPC なし"}
                >
                  <td className="px-1.5 py-0.5 tabular-nums whitespace-nowrap font-mono">{r.kcode}</td>
                  <td className="px-1.5 py-0.5 leading-tight">{r.name}</td>
                  <td className="px-1.5 py-0.5 text-right tabular-nums font-medium" title={agg.note(r.n_main, "件")}>
                    {r.n_main > 0 ? agg.fmtCount(r.n_main) : <span className="text-slate-300">0</span>}
                  </td>
                  <td className="px-1.5 py-0.5 text-right tabular-nums text-slate-600" title={agg.note(r.n_cases, "件")}>{agg.fmtCount(r.n_cases)}</td>
                </tr>
              );
            })}
            {/* [2026-09-29] メインの K が無い退院。退院数の列を足すと主表の退院数になる */}
            {data && (
              <tr className="border-t-2 border-slate-300 bg-slate-50 text-slate-600">
                <td className="px-1.5 py-0.5" />
                <td className="px-1.5 py-0.5 leading-tight">{NONE_LABEL[type]}</td>
                <td className="px-1.5 py-0.5 text-right tabular-nums" title={agg.note(data.none_main, "件")}>{agg.fmtCount(data.none_main)}</td>
                <td className="px-1.5 py-0.5 text-right tabular-nums text-slate-300">—</td>
              </tr>
            )}
            {data && (
              <tr className="bg-slate-100 font-semibold">
                <td className="px-1.5 py-0.5" />
                <td className="px-1.5 py-0.5">合計 (= 主表の退院数)</td>
                <td className="px-1.5 py-0.5 text-right tabular-nums">{agg.fmtCount(data.total_adm)}</td>
                <td className="px-1.5 py-0.5 text-right tabular-nums text-slate-300">—</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function KcodeTablesRow() {
  const f = useFilters();
  // [分岐該当のみ 2026-07-18] 選択中分岐の候補K (公式対応表) に一致する実績Kだけに絞る
  const [branchOnly, setBranchOnly] = useState(false);
  const branchSelectable = (f.dpcLevel >= 11 && !!f.selectedDpc11) || (f.dpcLevel >= 12 && !!f.selectedDpc12);
  return (
    <div className="mt-2">
      <div className="text-[11px] text-slate-500 mb-1 leading-tight">
        ▸ 実績Kコード: 手術・手術処置等1/2 で実際に行われた手術/処置Kコード
        (DPC様式1 datijidpcnyudatasub syubetu 0003/0004 由来、件数降順)。
        共通 filter (期間 / 診療科 / 医師 / 疾患 / 予定緊急) に連動。マスタにあって実績 0 のものは非表示。
        <span className="ml-1 text-amber-700">※この表は既定では処置1/2 の実施分が<b>すべて</b>並びます（分岐を決めた処置以外の<b>併施</b>も含む。
        14桁の分岐はこのうち最上位の処置で採番。例: 敗血症「4あり」でも併施の中心静脈注射・人工呼吸が載る）。
        下のチェックで分岐該当だけに絞れます。<b>主表の処置1/2 列のホバー</b>は 2026-07-22 から
        「分岐に該当する処置」と「併施」を分けて表示しています。</span>
        <span className="ml-1 text-emerald-700">行クリックで「最頻 DPC」を主表・DPC包括点数に流し込み（連動絞り込み）。</span>
        {branchSelectable && (
          <label className="ml-2 inline-flex items-center gap-1 text-[11px] text-slate-700 cursor-pointer select-none no-print">
            <input type="checkbox" checked={branchOnly} onChange={(e) => setBranchOnly(e.target.checked)} />
            <b>分岐該当の処置のみ</b> (併施を隠す・選択中の処置1/2分岐の公式候補Kに一致するものだけ)
          </label>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
        <KcodeTable
          type="ope"
          title="手術（実績K）"
          subtitle="(実際に行われた手術Kコード、ダミー除外)"
        />
        <KcodeTable
          type="proc1"
          title="手術・処置等1（実績K）"
          subtitle="(実際に行われた処置Kコード、008b由来)"
          branchOnly={branchOnly && branchSelectable}
        />
        <KcodeTable
          type="proc2"
          title="手術・処置等2（実績K）"
          subtitle="(実際に行われた処置Kコード、008b由来)"
          branchOnly={branchOnly && branchSelectable}
        />
      </div>
    </div>
  );
}
